import { useCallback, useEffect, useState } from 'react';
import { stateSetter } from '@/utils';
import { fetchSubscribedAdministrations } from './actions';
import {
	ISubscribedAdministration,
	SubscribedAdministrationsReturnType
} from './types';

// Administrațiile la care e abonat un utilizator (GET api/users/:id/subscribed-administrations), paginat.
// Fallback stabil ca identitate pentru `data` neîncărcat — un `[]` nou la fiecare
// randare re-declanșează în lanț efectele consumatorilor cu dependență pe `data`
// („Maximum update depth exceeded").
const EMPTY_LIST: never[] = [];

export function useUserSubscribedAdministrations<
	T extends ISubscribedAdministration | ISubscribedAdministration[]
>(
	userId: number | string | undefined = undefined,
	pageSize = 10
): SubscribedAdministrationsReturnType<T> {
	const [data, setData] = useState<T | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [page, setPage] = useState<number>(0);
	const [items, setItems] = useState<number>(pageSize);
	const [totalPages, setTotalPages] = useState<number>(1);
	const [totalItems, setTotalItems] = useState<number>(1);

	const handleResponse = (pageParam: number, itemsParam: number): void => {
		setTotalPages(pageParam);
		setTotalItems(itemsParam);
	};

	const getSubscriptions = useCallback(
		async (id: number | string, pageParam: number, itemsParam: number) => {
			try {
				setLoading(true);
				const response = await fetchSubscribedAdministrations(
					id,
					pageParam,
					itemsParam,
					handleResponse
				);
				setData(response as T);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoading(false);
			}
		},
		[setData]
	);

	useEffect(() => {
		if (userId === undefined) {
			setLoading(false);
			return;
		}
		getSubscriptions(userId, page, items);
	}, [userId, page, items, getSubscriptions]);

	return {
		data: data || (EMPTY_LIST as ISubscribedAdministration[] as T),
		setData: setData as stateSetter<T>,
		getSubscriptions,
		loading,
		items,
		page,
		setItems,
		setPage,
		totalPages,
		totalItems
	};
}
